import React, { useEffect, useState } from "react";
import { fetchRSOGameSummary, fetchRSOHistory, fetchRSOTimeline } from "@shared/api";
import { regionV4toV5 } from "@shared/utils";
import { useApi } from "@shared/api/hooks";
import { Match, MatchSummary } from "@shared/types/riot";
import { GameSummaryRSOView } from "@shared/modals/ImportGameModal/GameSummaryView";
import { useRSOToken } from "@shared/states/rsoState";
import { playbookState, resetMapState } from "@shared/states/frameState";
import { closeAllModals, matchState } from "@shared/states/uiStates";
import { saveNewCustomGame } from "@shared/api/custom_game";
import { RiotLoginButton } from "@shared/Toolbar/AuthButton/RiotLoginButton";

interface ImportCustomGameProps {}

const ImportCustomGame: React.FC<ImportCustomGameProps> = (props) => {
	const { loading, request } = useApi(fetchRSOHistory);
	const [history, setHistory] = useState<string[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [isImporting, setIsImporting] = useState(false);
	const { request: requestSaveNewCustomGame } = useApi(saveNewCustomGame);
	const rso = useRSOToken();
	const { loading: loadingTimeline, request: requestTimeline } = useApi(fetchRSOTimeline);

	useEffect(() => {
		(async () => {
			if (!rso?.token) return;

			const response = await request(rso?.token.token, regionV4toV5(rso.token.region), "420");

			if (!response?.history) {
				setError("Failed to load game history. Please reconnect your Riot account.");
				return;
			}
			setHistory(response.history);
		})();
	}, [rso?.token]);

	useEffect(() => {
		matchState.loading = isImporting;
		return () => {
			matchState.loading = false;
		};
	}, [isImporting]);

	const handleClick = async (summary: MatchSummary) => {
		if (!summary.platformId) {
			return;
		}

		setError(null);
		setIsImporting(true);
		try {
			const timeline = await requestTimeline(
				rso.token?.token!,
				regionV4toV5(summary.platformId),
				summary.platformId + "_" + summary.gameId,
			);
			if (!timeline) {
				setError("Failed to load the game. Please try again.");
				return;
			}

			const match: Match = {
				summary,
				timeline: timeline.timeline,
			};

			const saved_game = await requestSaveNewCustomGame({
				game_id: match.summary.gameName!,
				game_provider: "custom",
				match,
			});

			if (!saved_game) {
				setError("Failed to save the game. Please try again.");
				return;
			}

			resetMapState();
			matchState.gameProvider = "custom";
			matchState.customGameId = saved_game.custom_game.id;
			matchState.match = match;
			playbookState.customGameId = saved_game.custom_game.id;
			playbookState.customMatchToken = saved_game.token;
			closeAllModals();
		} finally {
			setIsImporting(false);
		}
	};

	// Check if user has connected their Riot account
	if (!rso?.token) {
		return (
			<div className={"flex flex-col items-center justify-center gap-4"}>
				<div className={"text-slate-100"}>
					Importing Custom games requires connecting your Riot account.
				</div>
				<RiotLoginButton />
			</div>
		);
	}

	if (loading) {
		return <div className={"text-white"}>Loading Game History...</div>;
	}

	if (history.length === 0) {
		return (
			<div className={"text-white opacity-80"}>{error ?? "No recent games found."}</div>
		);
	}

	return (
		<div className={"max-h-96 overflow-y-scroll"}>
			{error && <div className={"text-red-400 mt-2"}>{error}</div>}
			{history?.map((gameId) => (
				<GameSummaryRSOView
					key={gameId}
					region={regionV4toV5(rso?.token?.region!)}
					gameId={gameId}
					playerId={rso.account?.puuid!}
					onClick={handleClick}
				/>
			))}
		</div>
	);
};

export { ImportCustomGame };
